import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Button,
  Typography,
  TextField,
  Grid,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { endpoints } from 'src/utils/axios';
import { postRequestForWebsite } from 'src/services';
import { useSnackbar } from 'notistack';
import { handleErrorMessages } from 'src/services/fetching';
import { useSelector } from 'react-redux';
import useHeroSection from 'src/hooks/use-homepage-data';

const ProductFeaturesSection = () => {
  const homePageData=useSelector((state) => state.website.homePage);
  const {fetchOnMount}=useHeroSection();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Content state - only text and colors, design stays the same
  const [formData, setformData] = useState({
    mainHeading: '',
    subHeading: '',
    description: '',
    imageUrl: '', // Added imageUrl field

    // Feature labels (keep same positions)
    features: [
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ],

    // Feature links (corresponding to features)
    featureLinks: [
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ],

    // Feature class names (corresponding to features)
    featureClassNames: [
      'Financial',
      'Close',
      'Report',
      'Product-Integration',
      'Post',
      'Office',
      'Estimating',
      'Bid',
      'Project',
      'Field',
      'Safety',
      'Document',
    ],

    // Colors only
    mainHeadingColor: '#000000',
    subHeadingColor: '#8BC34A',
    descriptionColor: '#666666',
    circleColor: '#8BC34A',
    featureTextColor: '#000000',
  });

  const handleContentChange = (field, value) => {
    setformData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = value;
    setformData(prev => ({
      ...prev,
      features: updatedFeatures
    }));
  };

  const handleFeatureLinkChange = (index, value) => {
    const updatedFeatureLinks = [...formData.featureLinks];
    updatedFeatureLinks[index] = value;
    setformData(prev => ({
      ...prev,
      featureLinks: updatedFeatureLinks
    }));
  };

   useEffect(() => {
        fetchOnMount();
        }, [fetchOnMount]);
      useEffect(() => {
        if (homePageData?.product_feature_section) {
          // Create a deep copy of the community section to avoid read-only property issues
          const productSection = JSON.parse(JSON.stringify(homePageData.product_feature_section));

          // Initialize featureLinks array if it doesn't exist in the loaded data
          if (!productSection.featureLinks) {
            productSection.featureLinks = Array(12).fill('');
          }

          // Initialize featureClassNames array if it doesn't exist in the loaded data
          if (!productSection.featureClassNames) {
            productSection.featureClassNames = [
              'Financial',
              'Close',
              'Report',
              'Product-Integration',
              'Post',
              'Office',
              'Estimating',
              'Bid',
              'Project',
              'Field',
              'Safety',
              'Document',
            ];
          }

          setformData(prev => ({
            ...prev,
            ...productSection
          }));
        }
      }, [homePageData])

  const handleSave = async () => {

     setLoading(true);

         try {
           // Create a FormData object for file uploads
           const formDataToSend = new FormData();

           // Append all form data
           Object.entries(formData).forEach(([key, value]) => {
             // Skip heroImage as we'll handle it separately
             if (key !== 'heroImage') {
               // Handle nested objects like primaryButton and secondaryButton
               if (typeof value === 'object' && value !== null && !(value instanceof File)) {
                 formDataToSend.append(key, JSON.stringify(value));
               } else {
                 formDataToSend.append(key, value);
               }
             }
           });

           // Append the hero image if it exists
           if (formData.heroImage) {
             formDataToSend.append('heroImage', formData.heroImage);
           }

           const response = await postRequestForWebsite(
             endpoints.admin_dashboard.website.home_page.hero_section,
             // formDataToSend,
             // dataForHeroSection,
             {
               attributes:{
                ...JSON.parse(JSON.stringify(homePageData)),
                 product_feature_section: JSON.parse(JSON.stringify(formData))
               }
             },
             true
           );

           if (response.metadata?.success) {
             enqueueSnackbar({
               variant: 'success',
               message: response.metadata.message,
             });
           } else {
             handleErrorMessages(response.message || 'An error occurred.');
           }
         } catch (err) {
           console.error('Error:', err);
           handleErrorMessages(err.message || 'Submission failed.');
         } finally {
           setLoading(false);
         }

  };




  return (

    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
          <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            Product Features Section
          </Typography>
        </Stack>

          <Stack spacing={3}>

            <Divider />

            {/* Main Content Fields */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Main Heading"
                  value={formData.mainHeading}
                  onChange={(e) => handleContentChange('mainHeading', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Sub Heading"
                  value={formData.subHeading}
                  onChange={(e) => handleContentChange('subHeading', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleContentChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Image URL"
                  value={formData.imageUrl || ""}
                  onChange={(e) => handleContentChange('imageUrl', e.target.value)}
                  fullWidth
                  placeholder="Enter the URL of your image (e.g., https://example.com/image.jpg)"
                  helperText="Enter a valid image URL"
                />
                {formData.imageUrl && (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 400,
                      height: 200,
                      mt: 2,
                      mb: 2,
                      mx: 'auto',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <Box
                      component="img"
                      src={formData.imageUrl}
                      alt="Image Preview"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        display: 'none',
                        color: 'text.disabled',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      Invalid image URL
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            <Divider />

            {/* Color Settings */}
            <Typography variant="h6">Color Settings</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Main Heading Color"
                  type="color"
                  value={formData.mainHeadingColor}
                  onChange={(e) => handleContentChange('mainHeadingColor', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Sub Heading Color"
                  type="color"
                  value={formData.subHeadingColor}
                  onChange={(e) => handleContentChange('subHeadingColor', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Description Color"
                  type="color"
                  value={formData.descriptionColor}
                  onChange={(e) => handleContentChange('descriptionColor', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Green Circle Color"
                  type="color"
                  value={formData.circleColor}
                  onChange={(e) => handleContentChange('circleColor', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Feature Text Color"
                  type="color"
                  value={formData.featureTextColor}
                  onChange={(e) => handleContentChange('featureTextColor', e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Divider />

            {/* Feature Labels and Links */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Typography variant="h6">Feature Labels and Links</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Iconify icon="eva:info-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
                Each feature can have a label text and optional URL
              </Typography>
            </Stack>
            <Grid container spacing={3}>
              {formData.features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                        Feature {index + 1}
                      </Typography>

                      <TextField
                        label="Feature Label"
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ mb: 1 }}
                      />

                      <TextField
                        label="Feature URL"
                        value={formData.featureLinks[index]}
                        onChange={(e) => handleFeatureLinkChange(index, e.target.value)}
                        placeholder="https://example.com"
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <Iconify icon="eva:link-fill" sx={{ color: 'text.disabled', mr: 1, width: 20, height: 20 }} />
                          ),
                        }}
                      />
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>


        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="eva:save-fill" />}
            onClick={handleSave}
          >
            Update
          </Button>
        </Stack>

      </CardContent>
    </Card>
  );
};


export default ProductFeaturesSection;
